import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Check } from 'lucide-react';
import HeroFlowAnimation from './HeroFlowAnimation';
import { HERO } from '../config/siteContent';

/**
 * HeroSection - light theme.
 *
 * Layout (top → bottom):
 *   1. Glass badge row (VAN · 39,000+ Partners · 24/7 Support)
 *   2. Italic tagline (matches reference site, sits ABOVE the headline)
 *   3. Big display headline + sky gradient accent
 *   4. Subheading (concrete promise + numbers)
 *   5. Dual CTAs (primary sky / secondary outline)
 *   6. Reassurance row
 *   7. ETL flow diagram
 *   8. Scroll indicator
 *
 * Background note (May 2026): the original `AnimatedBackground` floating-
 * particle field was removed. It was distracting and competed with the ETL
 * diagram - which uses similar (but in-context) particles. The hero now sits
 * on a soft gradient via the `mesh-sky-bg` utility.
 *
 * Animation note: entry animations were softened - `initial.opacity` is
 * 0.5 instead of 0 so content doesn't BLINK in when scrolling/loading.
 * Translate distances reduced from 24px → 12px for gentler reveal.
 */
const HeroSection = () => {
  return (
    <section
      id="top"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-28 pb-32 lg:pb-20 mesh-sky-bg"
    >
      {/*
       * Fluid container: content width scales with viewport, capping at
       * 1600px on huge monitors. Below xl this stays compact for readability;
       * above xl it expands so the hero feels substantial on wide screens.
       */}
      <div
        className="relative z-10 mx-auto px-6 text-center w-full"
        style={{ maxWidth: 'min(95vw, 1800px)' }}
      >
        {/* Badge row - softer entry */}
        <motion.div
          className="inline-flex flex-wrap justify-center gap-2.5 mb-8"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
          }}
        >
          {HERO.badges.map((badge, idx) => (
            <motion.span
              key={idx}
              variants={{
                hidden: { opacity: 0.5, y: 6 },
                visible: { opacity: 1, y: 0 },
              }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="badge-glass"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent-primary opacity-60 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-accent-primary" />
              </span>
              {badge.text}
            </motion.span>
          ))}
        </motion.div>

        {/*
         * Fluid headline. Kerning loosened from -0.03em → -0.01em so words
         * don't appear to run together. Still slightly tight for design feel.
         */}
        <motion.h1
          initial={{ opacity: 0.5, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="font-display font-bold leading-[1.05] tracking-[-0.01em] text-text-primary mb-6 text-balance lg:whitespace-nowrap"
          style={{ fontSize: 'clamp(1.75rem, 3.4vw, 4rem)' }}
        >
          {HERO.headlineAccentFirst ? (
            <>
              <span className="text-gradient-sky font-extrabold">
                {HERO.headlineAccent}
              </span>{' '}
              {HERO.headline}
            </>
          ) : (
            <>
              {HERO.headline}{' '}
              <span className="text-gradient-sky font-extrabold">
                {HERO.headlineAccent}
              </span>
            </>
          )}
        </motion.h1>

        {/* Subheading - fluid + readable, slightly relaxed leading */}
        <motion.p
          initial={{ opacity: 0.5, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.65, ease: 'easeOut' }}
          className="text-text-secondary mx-auto leading-relaxed mb-10 text-pretty lg:whitespace-nowrap"
          style={{ fontSize: 'clamp(0.95rem, 1.45vw, 1.75rem)' }}
        >
          {HERO.subheading}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0.5, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8, ease: 'easeOut' }}
          className="flex flex-col sm:flex-row gap-3 justify-center items-center"
        >
          <a href="#solutions" className="btn-primary group">
            {HERO.primaryCTA}
            <ArrowRight
              size={16}
              className="transition-transform duration-200 group-hover:translate-x-1"
            />
          </a>
          <Link to="/contact" className="btn-secondary group">
            <Calendar size={16} />
            {HERO.secondaryCTA}
          </Link>
        </motion.div>

        {/* Reassurance row - concrete, varied (no repeat of "Trusted since 2000") */}
        <motion.div
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.0 }}
          className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-text-muted"
        >
          {HERO.reassurance.map((item) => (
            <span key={item} className="inline-flex items-center gap-1.5">
              <Check
                size={14}
                className="text-accent-primary"
                strokeWidth={3}
              />
              {item}
            </span>
          ))}
        </motion.div>

        {/* Live ETL flow animation - proves the headline by visually showing
           a document travel from your system through iConnect to a partner */}
        <motion.div
          initial={{ opacity: 0.5, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <HeroFlowAnimation />
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
